import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppContext } from '../../shared/AppContext';
import { getDefaultModules } from '../../../api/v2/TasklistsApi';
import { DefaultModuleDto } from '../../../dtos/DefaultModuleDto';
import { detectOutOfSync, syncGrants } from '../../../api/v2/TemplateSyncApi';
import type { TemplateSyncGrantResult } from '../../../dtos/TemplateSyncGrantResult';
import type { TemplateSyncGrantSelection } from '../../../dtos/TemplateSyncGrantSelection';
import type { TemplateSyncRunResult } from '../../../dtos/TemplateSyncRunResult';
import { PhaseStatus } from '../../../dtos/PhaseStatus';
import { PhaseWorkflowType } from '../../../dtos/PhaseWorkflowType';

type ModuleOption = { id: number; name: string };

export type TreeRowType = 'grant' | 'task' | 'subtask';

export interface TemplateSyncTreeRow {
    id: string;
    parentId: string | null;
    rowType: TreeRowType;
    name: string;
    // Grant identifiers (present on every row so we can group selections by grant)
    grantId: string;
    grantNumber?: string;
    institution?: string | null;
    fy?: number;
    // Task fields
    defaultPhaseId?: number;
    isExistingTask?: boolean;
    currentStatus?: PhaseStatus | null;
    workflowType?: PhaseWorkflowType | null;
    // Subtask fields
    defaultStepId?: number;
    isMissingSubtask?: boolean;
    // Instance ids — sent back to the API when a "different" row is selected
    phaseInstanceId?: string | null;
    stepInstanceId?: string | null;
    // Display fields populated when the instance differs from the template
    titleDiffers?: boolean;
    currentTitle?: string | null;
    templateTitle?: string | null;
    descriptionDiffers?: boolean;
    currentDescription?: string | null;
    templateDescription?: string | null;
    // Counts (for display on grant/task rows)
    missingTaskCount?: number;
    missingSubtaskCount?: number;
    differentTaskCount?: number;
    differentSubtaskCount?: number;
}

const parseModuleParam = (params: URLSearchParams): number | null => {
    const raw = params.get('module');
    if (!raw) return null;
    const id = parseInt(raw, 10);
    return Number.isFinite(id) ? id : null;
};

const grantRowKey = (grantId: string) => grantId;
const taskRowKey = (grantId: string, defaultPhaseId: number) => `${grantId}::task::${defaultPhaseId}`;
const subtaskRowKey = (grantId: string, defaultPhaseId: number, defaultStepId: number) =>
    `${grantId}::task::${defaultPhaseId}::step::${defaultStepId}`;

const buildTreeRows = (results: TemplateSyncGrantResult[]): TemplateSyncTreeRow[] => {
    const rows: TemplateSyncTreeRow[] = [];
    for (const g of results) {
        rows.push({
            id: grantRowKey(g.grantId),
            parentId: null,
            rowType: 'grant',
            // Combine grant number + institution so the column filter matches either.
            // The cellRender still draws them separately for display.
            name: g.shortInstitution ? `${g.grantNumber} ${g.shortInstitution}` : g.grantNumber,
            grantId: g.grantId,
            grantNumber: g.grantNumber,
            institution: g.shortInstitution,
            fy: g.fy,
            missingTaskCount: g.missingTaskCount,
            missingSubtaskCount: g.missingSubtaskCount,
            differentTaskCount: g.differentTaskCount,
            differentSubtaskCount: g.differentSubtaskCount
        });
        for (const t of g.tasks) {
            const taskId = taskRowKey(g.grantId, t.defaultPhaseId);
            rows.push({
                id: taskId,
                parentId: grantRowKey(g.grantId),
                rowType: 'task',
                name: t.name,
                grantId: g.grantId,
                defaultPhaseId: t.defaultPhaseId,
                isExistingTask: t.isExisting,
                currentStatus: t.currentStatus,
                workflowType: t.workflowType,
                phaseInstanceId: t.phaseId,
                titleDiffers: t.titleDiffers,
                currentTitle: t.currentTitle,
                templateTitle: t.templateTitle,
                missingSubtaskCount: t.subtasks.filter(s => s.isMissing).length,
                differentSubtaskCount: t.subtasks.filter(s => !s.isMissing).length
            });
            for (const s of t.subtasks) {
                rows.push({
                    id: subtaskRowKey(g.grantId, t.defaultPhaseId, s.defaultStepId),
                    parentId: taskId,
                    rowType: 'subtask',
                    name: s.name,
                    grantId: g.grantId,
                    defaultPhaseId: t.defaultPhaseId,
                    defaultStepId: s.defaultStepId,
                    isMissingSubtask: s.isMissing,
                    stepInstanceId: s.stepId,
                    titleDiffers: s.titleDiffers,
                    currentTitle: s.currentTitle,
                    templateTitle: s.templateTitle,
                    descriptionDiffers: s.descriptionDiffers,
                    currentDescription: s.currentDescription,
                    templateDescription: s.templateDescription
                });
            }
        }
    }
    return rows;
};

// Translate the flat list of selected tree-row keys into a per-grant
// selection payload the backend can act on. DevExtreme TreeList's recursive
// selection only stores the explicitly-clicked key in selectedRowKeys (it does
// not flatten descendants), so we walk ancestors here: a row is "effectively
// selected" when itself or any ancestor is in selectedKeys.
//
// Each effectively-selected row routes into one of four buckets:
//   - new task (missing phase)              -> defaultPhaseIds
//   - new subtask (missing step)            -> defaultStepIds
//   - different task (existing phase title) -> phaseIdsToUpdate
//   - different subtask (existing step)     -> stepIdsToUpdate
const buildSelections = (
    rows: TemplateSyncTreeRow[],
    selectedKeys: string[]
): TemplateSyncGrantSelection[] => {
    const byKey = new Map(rows.map(r => [r.id, r] as const));
    const selectedSet = new Set(selectedKeys);

    const isEffectivelySelected = (row: TemplateSyncTreeRow): boolean => {
        let cur: TemplateSyncTreeRow | undefined = row;
        while (cur) {
            if (selectedSet.has(cur.id)) return true;
            cur = cur.parentId ? byKey.get(cur.parentId) : undefined;
        }
        return false;
    };

    const byGrant = new Map<string, TemplateSyncGrantSelection>();
    const ensure = (grantId: string): TemplateSyncGrantSelection => {
        let entry = byGrant.get(grantId);
        if (!entry) {
            entry = { grantId, defaultPhaseIds: [], defaultStepIds: [], phaseIdsToUpdate: [], stepIdsToUpdate: [] };
            byGrant.set(grantId, entry);
        }
        return entry;
    };

    for (const row of rows) {
        if (!isEffectivelySelected(row)) continue;

        if (row.rowType === 'task') {
            // New task: create the phase and (recursively) all template steps under it.
            if (row.isExistingTask === false && row.defaultPhaseId != null) {
                ensure(row.grantId).defaultPhaseIds.push(row.defaultPhaseId);
                continue;
            }
            // Existing task whose title differs from the template: queue a name update on the phase instance.
            // (Selecting a task row also flows into its child subtask rows below.)
            if (row.isExistingTask === true && row.titleDiffers && row.phaseInstanceId) {
                ensure(row.grantId).phaseIdsToUpdate.push(row.phaseInstanceId);
            }
            continue;
        }

        if (row.rowType === 'subtask') {
            if (row.isMissingSubtask && row.defaultStepId != null) {
                ensure(row.grantId).defaultStepIds.push(row.defaultStepId);
            } else if (!row.isMissingSubtask && row.stepInstanceId) {
                ensure(row.grantId).stepIdsToUpdate.push(row.stepInstanceId);
            }
        }
    }

    return Array.from(byGrant.values()).filter(
        s => s.defaultPhaseIds.length > 0
            || s.defaultStepIds.length > 0
            || s.phaseIdsToUpdate.length > 0
            || s.stepIdsToUpdate.length > 0
    );
};

export const useTemplateSyncController = () => {
    const app = useAppContext();
    const [searchParams] = useSearchParams();

    const [modules, setModules] = useState<ModuleOption[]>([]);
    const [selectedModuleId, setSelectedModuleId] = useState<number | null>(() => parseModuleParam(searchParams));
    const [results, setResults] = useState<TemplateSyncGrantResult[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState<TemplateSyncRunResult | null>(null);

    const treeRows = useMemo(() => buildTreeRows(results), [results]);

    const pendingSelections = useMemo(
        () => buildSelections(treeRows, selectedRowKeys),
        [treeRows, selectedRowKeys]
    );
    const selectedGrantCount = pendingSelections.length;

    const detect = useCallback(async (moduleId: number) => {
        if (!moduleId) return;
        setLoading(true);
        setResults([]);
        setSelectedRowKeys([]);
        setSyncResult(null);
        try {
            const res = await detectOutOfSync(moduleId);
            if (res?.error) {
                app.errorToast(res.error.message);
                return;
            }
            setResults(res?.data ?? []);
        } finally {
            setLoading(false);
        }
    }, [app]);

    // Load modules on mount
    useEffect(() => {
        (async () => {
            const res = await getDefaultModules(true);
            if (res?.error || !res?.data) return;
            setModules(res.data.map((m: DefaultModuleDto) => ({ id: m.id, name: m.name })));
        })();
    }, []);

    // Auto-detect whenever the selected module changes (also fires on initial
    // mount when the URL contains a ?module= param).
    useEffect(() => {
        if (selectedModuleId) {
            detect(selectedModuleId);
        } else {
            setResults([]);
            setSelectedRowKeys([]);
            setSyncResult(null);
        }
    }, [selectedModuleId, detect]);

    const sync = useCallback(async () => {
        if (!selectedModuleId || pendingSelections.length === 0) {
            app.infoToast("Select at least one item to sync.");
            return;
        }
        setSyncing(true);
        setSyncResult(null);
        try {
            const res = await syncGrants(selectedModuleId, pendingSelections);
            if (res?.error) {
                app.errorToast(res.error.message);
                return;
            }
            if (res?.data) {
                setSyncResult(res.data);
                if (res.data.syncedCount > 0) {
                    app.infoToast(`Synced ${res.data.syncedCount} grant(s) successfully.`);
                }
                if (res.data.errorCount > 0) {
                    app.errorToast(`${res.data.errorCount} grant(s) failed to sync.`);
                }
            }
            await detect(selectedModuleId);
        } finally {
            setSyncing(false);
        }
    }, [selectedModuleId, pendingSelections, detect, app]);

    return {
        isSuper: app.UserIsSuper,
        modules,
        selectedModuleId,
        setSelectedModuleId,
        results,
        treeRows,
        selectedRowKeys,
        setSelectedRowKeys,
        selectedGrantCount,
        loading,
        syncing,
        syncResult,
        sync
    };
};
